import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Paper, Typography, Stack, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Switch, Select, MenuItem, TextField, TableSortLabel, TablePagination
} from '@mui/material'
import { fetchWithAuth } from '../utils/fetchWithAuth'

// --- Helper funkcie ---
function getMemberships(user) {
  if (Array.isArray(user?.memberships)) return user.memberships
  if (Array.isArray(user?.Memberships)) return user.Memberships
  const single =
    user?.membership ||
    user?.Membership ||
    user?.Membership_current ||
    user?.MembershipCurrent ||
    null
  if (single && typeof single === 'object') return [single]
  return []
}

function pickCurrentMembership(user) {
  const arr = getMemberships(user)
  if (!arr.length) return null
  return arr.reduce((best, m) => {
    const vt = m?.valid_to ?? m?.validTo ?? null
    const bt = best?.valid_to ?? best?.validTo ?? null
    if (vt == null && bt != null) return m
    if (vt != null && bt == null) return best
    const t = vt ? new Date(vt).getTime() : 0
    const tb = bt ? new Date(bt).getTime() : 0
    return t >= tb ? m : best
  }, arr[0])
}

function getMembershipTypeId(m) {
  if (!m) return ''
  const mt =
    m?.membershipType ||
    m?.MembershipType ||
    m?.membership_type ||
    m?.Membership_type ||
    null
  return (mt && (mt.id ?? mt.ID)) ?? m?.membership_type_id ?? ''
}

function getMembershipTypeLabel(m) {
  if (!m) return ''
  const mt =
    m?.membershipType ||
    m?.MembershipType ||
    m?.membership_type ||
    m?.Membership_type ||
    null
  return (mt && (mt.type || mt.name || mt.title)) || ''
}

function getValidToISODate(m) {
  const raw = m?.valid_to ?? m?.validTo ?? null
  if (!raw) return ''
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

function getIsActive(u) {
  return !!(u?.aktivny ?? u?.active ?? u?.is_active ?? u?.isActive)
}

function getCurrentMembershipLabelForUser(u) {
  const m = pickCurrentMembership(u) || u.membership || null
  return getMembershipTypeLabel(m)
}

function getCurrentValidToForUser(u) {
  const m = pickCurrentMembership(u) || u.membership || null
  return getValidToISODate(m)
}

function parseMoney(input) {
  if (input == null) return NaN
  let s = String(input).trim()
  s = s.replace(/[€\s]/g, '')
  if (s.includes(',') && s.includes('.')) {
    s = s.replace(/\./g, '').replace(',', '.')
  } else if (s.includes(',')) {
    s = s.replace(',', '.')
  }
  return Number(s)
}

// --- DOPLNENÉ helpery pre bezpečné UPDATE/INSERT len pri zmene ---
function getCurrentMembershipForUser(u) {
  return pickCurrentMembership(u) || u.membership || null
}

function getCurrentMembershipId(u) {
  const m = getCurrentMembershipForUser(u)
  if (!m) return ''
  return String(m.id ?? m.ID ?? '')
}

function getCurrentSnapshot(u) {
  const m = getCurrentMembershipForUser(u)
  return {
    aktivny: getIsActive(u),
    membership_id: getCurrentMembershipId(u) || '',
    membership_type_id: getMembershipTypeId(m) || '',
    valid_to: getValidToISODate(m) || ''
  }
}

function hasUserChange(u, draft) {
  const cur = getCurrentSnapshot(u)
  const sameAktivny = !!cur.aktivny === !!draft.aktivny
  const sameType = String(cur.membership_type_id || '') === String(draft.membership_type_id || '')
  const sameValidTo = String(cur.valid_to || '') === String(draft.valid_to || '')
  return !(sameAktivny && sameType && sameValidTo)
}

// --- JEDNOTKY bločkov za aktuálny mesiac ---
function sumReceiptsUnitsThisMonth(arr) {
  if (!Array.isArray(arr)) return 0
  const now = new Date()
  const ymNow = `${now.getFullYear()}-${now.getMonth()}`
  return arr.reduce((acc, r) => {
    const dateStr = r?.date ?? r?.createdAt ?? null
    const dt = dateStr ? new Date(dateStr) : null
    if (!dt || isNaN(dt.getTime())) return acc
    const ym = `${dt.getFullYear()}-${dt.getMonth()}`
    if (ym !== ymNow) return acc
    const v = parseMoney(r?.units ?? 0)
    return acc + (Number.isFinite(v) ? v : 0)
  }, 0)
}

export default function AdminCashbackLab() {
  const [me, setMe] = useState(null)
  const [users, setUsers] = useState([])
  const [types, setTypes] = useState([])
  const [filter, setFilter] = useState('')
  const [statusMsg, setStatusMsg] = useState('')
  const [drafts, setDrafts] = useState({})
  const [receiptsMap, setReceiptsMap] = useState({})
  const [orderBy, setOrderBy] = useState('id')
  const [order, setOrder] = useState('asc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const reloadUsers = async () => {
    try {
      const res = await fetchWithAuth('/admin/lab/users/all')
      const data = res.ok ? await res.json() : []
      setUsers(data)

      const initial = {}
      data.forEach(u => {
        const m = pickCurrentMembership(u) || u.membership || null
        initial[u.id] = {
          aktivny: getIsActive(u),
          membership_type_id: getMembershipTypeId(m) || '',
          valid_to: getValidToISODate(m) || ''
        }
      })
      setDrafts(initial)
    } catch (err) {
      console.error(err)
      setStatusMsg('Nepodarilo sa načítať používateľov.')
    }
  }

  const loadTypes = async () => {
    try {
      const res = await fetchWithAuth('/membership/types')
      let data = res.ok ? await res.json() : []
      // zoradí membership typy podľa id vzostupne
      data = data.sort((a, b) => Number(a.id) - Number(b.id))
      setTypes(data)
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth('/auth/me')
        if (res.ok) setMe(await res.json())
      } catch {}
      try {
        await Promise.all([reloadUsers(), loadTypes()])
      } catch (err) {
        console.error(err)
        setStatusMsg('Nepodarilo sa načítať dáta.')
      }
    })()
  }, [])

  const filteredUsers = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return users
    return users.filter(u =>
      String(u.id).includes(q) ||
      (u.meno || u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    )
  }, [users, filter])

  const displayedUsers = useMemo(() => {
    const val = (u) => {
      switch (orderBy) {
        case 'id': return Number(u.id) || 0
        case 'name': return ((u.meno || u.name || '') + ' ' + (u.priezvisko || '')).trim().toLowerCase()
        case 'active': return getIsActive(u) ? 1 : 0
        case 'membershipType': return (getCurrentMembershipLabelForUser(u) || '').toLowerCase()
        case 'validTo': {
          const v = getCurrentValidToForUser(u)
          return v ? new Date(v).getTime() : 0
        }
        case 'receipts': return Array.isArray(u.receipts) ? u.receipts.length : 0
        default: return 0
      }
    }

    const sorted = [...filteredUsers].sort((a, b) => {
      const va = val(a), vb = val(b)
      let cmp = typeof va === 'string' ? va.localeCompare(vb) : (va > vb ? 1 : va < vb ? -1 : 0)
      return order === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [filteredUsers, order, orderBy])

  const paginatedUsers = displayedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  // --- FIX: patchDraft zachová aj aktivny ---
  const patchDraft = (id, patch) =>
    setDrafts(prev => {
      const current = prev[id] || {}
      const withAktivny = patch.hasOwnProperty('aktivny')
        ? patch
        : { aktivny: current.aktivny, ...patch }
      return { ...prev, [id]: { ...current, ...withAktivny } }
    })

  const isMe = (id) => me && String(me.id) === String(id)

  const handleSaveAll = async () => {
    try {
      const items = Object.entries(drafts)
        .map(([id, d]) => {
          const u = users.find(x => String(x.id) === String(id))
          if (!u) return null
          if (!hasUserChange(u, d)) return null

          const cur = getCurrentSnapshot(u)
          const out = { id: Number(id) }

          if (d.hasOwnProperty('aktivny') && !!cur.aktivny !== !!d.aktivny) {
            out.aktivny = !!d.aktivny
          }

          const typeChanged = String(cur.membership_type_id || '') !== String(d.membership_type_id || '')
          const validToChanged = String(cur.valid_to || '') !== String(d.valid_to || '')

          if (typeChanged || validToChanged) {
            if (cur.membership_id) {
              out.membership_id = Number(cur.membership_id)
            }
            if (typeChanged) {
              out.membership_type_id = d.membership_type_id ? Number(d.membership_type_id) : null
            }
            if (validToChanged) {
              out.valid_to = d.valid_to || null
            }
          }

          if (Object.keys(out).length === 1) return null
          return out
        })
        .filter(Boolean)

      if (!items.length) {
        setStatusMsg('Žiadne zmeny na uloženie.')
        return
      }

      const res = await fetchWithAuth('/admin/lab/users/bulk-update', {
        method: 'PATCH',
        body: JSON.stringify({ users: items })
      })

      if (res.ok) {
        setStatusMsg('Zmeny uložené.')
        await reloadUsers()
      } else {
        setStatusMsg('Chyba pri ukladaní zmien.')
      }
    } catch (err) {
      console.error(err)
      setStatusMsg('Chyba pri ukladaní zmien.')
    }
  }

  const handleCancelAll = async () => {
    await reloadUsers()
    setStatusMsg('Zmeny zrušené.')
  }

  const handleUploadReceipt = async (userId, file) => {
    if (!file) return
    if (!isMe(userId)) {
      setStatusMsg('Upload bločku pre iného používateľa nie je povolený.')
      return
    }
    try {
      const fd = new FormData()
      fd.append('file', file)

      const upRes = await fetchWithAuth('/receipt/upload', {
        method: 'POST',
        body: fd
      })

      const upData = upRes.ok ? await upRes.json() : {}
      const receiptId = upData?.id

      if (receiptId) {
        try {
          await fetchWithAuth('/receipt/ocr', {
            method: 'POST',
            body: JSON.stringify({ receiptId })
          })
        } catch {}
      }

      setReceiptsMap(prev => {
        const arr = prev[userId] || []
        return {
          ...prev,
          [userId]: [{ id: receiptId || `temp-${Date.now()}`, name: file.name, size: file.size }, ...arr]
        }
      })

      setStatusMsg(`Bloček nahratý pre #${userId}.`)
    } catch (err) {
      console.error(err)
      setStatusMsg('Upload bločku zlyhal.')
    }
  }

  const handleAddReceiptRecord = async (userId) => {
    const rawPrice = window.prompt('Cena bločku (€):')
    if (rawPrice == null || rawPrice === '') {
      setStatusMsg('Vloženie bločku zrušené.')
      return
    }

    const total_price = parseMoney(rawPrice)
    if (!Number.isFinite(total_price) || total_price <= 0) {
      setStatusMsg('Neplatná suma. Zadaj napr. 10.5 alebo 10,50')
      return
    }

    const rawUnits = window.prompt('Množstvo (litre alebo kWh):')
    if (rawUnits == null || rawUnits === '') {
      setStatusMsg('Vloženie bločku zrušené.')
      return
    }

    const units = parseMoney(rawUnits)
    if (!Number.isFinite(units) || units <= 0) {
      setStatusMsg('Neplatné množstvo. Zadaj číslo.')
      return
    }

    const rawType = window.prompt('Druh energie (fuel/electric):', 'fuel')
    const fuel_type = rawType && rawType.toLowerCase().startsWith('e') ? 'electric' : 'fuel'

    const description = window.prompt('Popis bločku (nepovinné):') || ''
    const dateStr = window.prompt('Dátum (YYYY-MM-DD, nepovinné):') || ''
    const date = dateStr ? new Date(dateStr) : null

    if (dateStr && Number.isNaN(date.getTime())) {
      setStatusMsg('Neplatný dátum. Použi formát YYYY-MM-DD.')
      return
    }

    try {
      const res = await fetchWithAuth(`/admin/lab/users/${userId}/receipt`, {
        method: 'POST',
        body: JSON.stringify({
          total_price,
          units,
          fuel_type,
          description,
          date: date ? date.toISOString() : null
        })
      })

      if (res.ok) {
        setStatusMsg(`Bloček pridaný pre používateľa #${userId}.`)
        await reloadUsers()
      } else {
        setStatusMsg(`Pridanie bločku zlyhalo: ${res.status}`)
      }
    } catch (err) {
      console.error(err)
      setStatusMsg('Pridanie bločku zlyhalo.')
    }
  }

  const handleRequestSort = (key) => {
    if (orderBy === key) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(key)
      setOrder('asc')
    }
  }

  return (
    <Box
      sx={{
        background: '#ededed',
        padding: 1.5,
        border: '1.5px solid #107c6b',
        borderRadius: '10px',
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5">Admin Cashback Lab (inline)</Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Filtrovať ID / meno / email"
            value={filter}
            onChange={e => {
              setFilter(e.target.value)
              setPage(0)
            }}
          />
          <Button variant="outlined" onClick={handleCancelAll}>Zrušiť</Button>
          <Button variant="contained" onClick={handleSaveAll}>Uložiť všetky zmeny</Button>
        </Stack>
      </Stack>

      {statusMsg && <Chip label={statusMsg} sx={{ mb: 1 }} />}

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid #c7c7c7',
          borderRadius: '10px',
          boxShadow: 'none'
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'id' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Meno / Email
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === 'active' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'active'}
                  direction={orderBy === 'active' ? order : 'asc'}
                  onClick={() => handleRequestSort('active')}
                >
                  Aktívny
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === 'membershipType' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'membershipType'}
                  direction={orderBy === 'membershipType' ? order : 'asc'}
                  onClick={() => handleRequestSort('membershipType')}
                >
                  Typ členstva
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === 'validTo' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'validTo'}
                  direction={orderBy === 'validTo' ? order : 'asc'}
                  onClick={() => handleRequestSort('validTo')}
                >
                  Platnosť do
                </TableSortLabel>
              </TableCell>

              <TableCell sortDirection={orderBy === 'receipts' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'receipts'}
                  direction={orderBy === 'receipts' ? order : 'asc'}
                  onClick={() => handleRequestSort('receipts')}
                >
                  Bločky
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedUsers.map(u => {
              const d = drafts[u.id] || {}
              const currentM = pickCurrentMembership(u) || u.membership || null
              const typeLabel = getMembershipTypeLabel(currentM)
              const receipts = (u.receipts && Array.isArray(u.receipts) ? u.receipts : []) || receiptsMap[u.id] || []
              const receiptsUnits = sumReceiptsUnitsThisMonth(receipts)

              return (
                <TableRow key={u.id} hover>
                  <TableCell>{u.id}</TableCell>

                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong>{u.meno || u.name || '—'}</strong>
                      <span style={{ opacity: 0.8, fontSize: 12 }}>{u.email || '—'}</span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <Switch
                      checked={!!d.aktivny}
                      onChange={e => patchDraft(u.id, { aktivny: e.target.checked })}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 260 }}>
                    <Select
                      size="small"
                      fullWidth
                      value={d.membership_type_id || ''}
                      onChange={e => patchDraft(u.id, { membership_type_id: e.target.value })}
                    >
                      <MenuItem value=""><em>—</em></MenuItem>
                      {types.map(t => (
                        <MenuItem key={t.id} value={t.id}>
                          #{t.id} • {(t.type || t.name || t.title || 'Typ')}
                        </MenuItem>
                      ))}
                    </Select>

                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                      Aktuálne: {typeLabel || '—'}
                    </div>
                  </TableCell>

                  <TableCell sx={{ minWidth: 170 }}>
                    <TextField
                      type="date"
                      size="small"
                      value={d.valid_to || ''}
                      onChange={e => patchDraft(u.id, { valid_to: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      helperText="len UI"
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 300 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button component="label" size="small" variant="outlined">
                        Nahrať
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          hidden
                          onChange={evt => evt.target.files?.[0] && handleUploadReceipt(u.id, evt.target.files[0])}
                        />
                      </Button>

                      <Button size="small" variant="outlined" onClick={() => handleAddReceiptRecord(u.id)}>
                        Pridať záznam
                      </Button>

                      <div style={{ fontSize: 12, opacity: 0.8 }}>
                        {receipts?.length ? `${receipts.length}× • ${receiptsUnits.toFixed(2)} jednotiek` : '—'}
                      </div>
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}

            {displayedUsers.length === 0 && (
              <TableRow><TableCell colSpan={6}>Žiadne záznamy</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TablePagination
          component="div"
          count={displayedUsers.length}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Na stránku"
        />
      </Box>
    </Box>
  )
}